// Operator-configurable sidebar visibility.
//
// The gateway ships every page it supports, but a given deployment only ever
// touches a handful — so the sidebar starts on a curated subset and Gateway
// Settings decides the rest. The choice lives in the `sidebarSections` key of
// the workspace-settings JSON blob (workspace-wide, not per-user), so the
// whole team sees the same nav.
//
// This gates the *sidebar only*. Hidden pages keep their routes, their deep
// links, their AI navigate_to target and their topbar-search entries — hiding
// a page declutters the nav, it never makes the page unreachable.

const NAV_VISIBILITY_SETTING_KEY = "sidebarSections";

// Pages that stay out of the sidebar until someone ticks them back on.
// Everything not listed here is visible by default, including nav items added
// after a workspace has already saved its choices (an id absent from the
// stored map falls back to this default).
const DEFAULT_HIDDEN_NAV_ITEMS = Object.freeze([
  "projects-list",
  "platform-license",
  "platform-updates",
  "platform-tls",
  "ignition-gateways",
  "vtscada-connections",
  "oauth-apps",
  "modbus-devices",
  "enip-devices",
  "pi-connections",
  "plc-protocol-services",
  "opcua-server",
  "s7comm-server",
]);

const DEFAULT_HIDDEN = new Set(DEFAULT_HIDDEN_NAV_ITEMS);

// Lazy require: navigation → sections → platform.js (the Gateway Settings
// page that renders the checklist) → here, so a static import would close the
// cycle. Same trick as shared.js's getAllNavItems.
function navigationModule() {
  return require("./navigation");
}

export function defaultNavItemVisibility(id) {
  return !DEFAULT_HIDDEN.has(id);
}

// The stored value comes from a free-form JSON blob, so anything that isn't a
// boolean keyed by a known section id is dropped rather than trusted.
export function normalizeNavVisibility(raw) {
  if (!raw || typeof raw !== "object") return {};
  const { ROUTES } = navigationModule();
  const out = {};
  Object.keys(raw).forEach((id) => {
    if (!ROUTES[id]) return;
    if (typeof raw[id] !== "boolean") return;
    out[id] = raw[id];
  });
  return out;
}

export function isNavItemVisible(visibility, id) {
  const explicit = visibility?.[id];
  return typeof explicit === "boolean" ? explicit : defaultNavItemVisibility(id);
}

// The groups the checklist offers: everything the sidebar would consider for
// this user, before visibility is applied. Items this user can't reach at all
// (admin-only, unlicensed, other SKU) stay out — toggling them would be a
// no-op, and their stored values survive untouched because the checklist
// merges into the existing map instead of replacing it.
export function listToggleableNavGroups({ isAdmin, licensingEnabled, products }) {
  const { NAV_GROUPS, isNavItemAvailable } = navigationModule();
  return NAV_GROUPS
    .map((group) => ({
      title: group.title,
      items: group.items.filter((item) =>
        isNavItemAvailable(item, { isAdmin, licensingEnabled, products })
      ),
    }))
    .filter((group) => group.items.length > 0);
}

export { DEFAULT_HIDDEN_NAV_ITEMS, NAV_VISIBILITY_SETTING_KEY };
